import config from "../Config";
import { getSession } from "../utils/utils";
import * as Sentry from "@sentry/react";

/**
 * Chat API Client
 * Handles all HTTP requests to chat endpoints
 */
class ChatApiClient {
    constructor() {
        this.baseUrl = config.apiBaseUrl;
        this.timeout = 30000; // 30 seconds timeout
    }

    /**
     * Get authorization headers
     */
    getAuthHeaders() {
        const session = getSession();
        if (!session?.token) {
            throw new Error("No authentication token found");
        }

        return {
            "Content-Type": "application/json",
            "access-token": session.token,
        };
    }

    /**
     * Make HTTP request with error handling
     */
    async makeRequest(url, options = {}) {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), this.timeout);

        try {
            const response = await fetch(`${this.baseUrl}${url}`, {
                ...options,
                signal: controller.signal,
                headers: {
                    ...this.getAuthHeaders(),
                    ...options.headers,
                },
            });

            clearTimeout(timeoutId);

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                const error = new Error(errorData.detail || `HTTP ${response.status}: ${response.statusText}`);
                error.status = response.status;
                error.data = errorData;
                throw error;
            }

            return await response.json();
        } catch (error) {
            clearTimeout(timeoutId);
            
            if (error.name === 'AbortError') {
                const timeoutError = new Error('Request timeout');
                timeoutError.isTimeout = true;
                throw timeoutError;
            }

            // Log error to Sentry
            Sentry.captureException(error, {
                tags: { service: 'chat-api' },
                extra: { url, options }
            });

            throw error;
        }
    }

    /**
     * Send chat message to the backend
     * @param {string} message - User message
     * @param {string} sessionId - Optional session ID
     * @returns {Promise<Object>} API response
     */
    async sendMessage(message, sessionId = null) {
        if (!message || typeof message !== 'string') {
            throw new Error('Message is required and must be a string');
        }

        const payload = {
            message: message.trim(),
            ...(sessionId && { session_id: sessionId })
        };

        try {
            const response = await this.makeRequest(config.chat.sendMessage, {
                method: 'POST',
                body: JSON.stringify(payload),
            });

            return response;
        } catch (error) {
            console.error('Error sending chat message:', error);
            
            // Return fallback response for better UX
            if (error.isTimeout) {
                throw new Error('The request is taking longer than expected. Please try again.');
            }
            
            if (error.status === 401) {
                throw new Error('Authentication failed. Please log in again.');
            }
            
            if (error.status === 429) {
                throw new Error('Too many requests. Please wait a moment before sending another message.');
            }
            
            throw new Error('Failed to send message. Please try again.');
        }
    }

    /**
     * Get chat history for user
     * @param {string} sessionId - Optional session ID filter
     * @param {number} limit - Number of messages to return (1-100)
     * @param {number} offset - Number of messages to skip
     * @returns {Promise<Object>} Chat history response
     */
    async getChatHistory(sessionId = null, limit = 50, offset = 0) {
        if (limit < 1 || limit > 100) {
            throw new Error('Limit must be between 1 and 100');
        }

        if (offset < 0) {
            throw new Error('Offset must be non-negative');
        }

        const params = new URLSearchParams({
            limit: limit.toString(),
            offset: offset.toString(),
        });

        if (sessionId) {
            params.append('session_id', sessionId);
        }

        try {
            const response = await this.makeRequest(`${config.chat.getHistory}?${params}`, {
                method: 'GET',
            });

            return response;
        } catch (error) {
            console.error('Error fetching chat history:', error);
            
            if (error.status === 401) {
                throw new Error('Authentication failed. Please log in again.');
            }
            
            throw new Error('Failed to load chat history. Please try again.');
        }
    }

    /**
     * Get user context for chatbot
     * @returns {Promise<Object>} User context response
     */
    async getUserContext() {
        try {
            const response = await this.makeRequest(config.chat.getUserContext, {
                method: 'GET',
            });

            return response;
        } catch (error) {
            console.error('Error fetching user context:', error);
            
            if (error.status === 401) {
                throw new Error('Authentication failed. Please log in again.');
            }
            
            throw new Error('Failed to load user context.');
        }
    }

    /**
     * Retry mechanism for failed requests
     * @param {Function} apiCall - API call function
     * @param {number} maxRetries - Maximum number of retries
     * @param {number} delay - Delay between retries in ms
     */
    async withRetry(apiCall, maxRetries = 3, delay = 1000) {
        let lastError;

        for (let attempt = 1; attempt <= maxRetries; attempt++) {
            try {
                return await apiCall();
            } catch (error) {
                lastError = error;
                
                // Don't retry authentication errors or client errors (4xx)
                if (error.status >= 400 && error.status < 500) {
                    throw error;
                }

                if (attempt === maxRetries) {
                    break;
                }

                // Exponential backoff
                const waitTime = delay * Math.pow(2, attempt - 1);
                console.log(`API call failed, retrying in ${waitTime}ms (attempt ${attempt}/${maxRetries})`);
                await new Promise(resolve => setTimeout(resolve, waitTime));
            }
        }

        throw lastError;
    }

    /**
     * Get user's chat sessions
     * @param {number} limit - Maximum number of sessions to return
     * @param {number} offset - Number of sessions to skip
     * @returns {Promise<Object>} Sessions data
     */
    async getSessions(limit = 50, offset = 0) {
        if (limit < 1 || limit > 100) {
            throw new Error('Limit must be between 1 and 100');
        }

        if (offset < 0) {
            throw new Error('Offset must be non-negative');
        }

        const params = new URLSearchParams({
            limit: limit.toString(),
            offset: offset.toString(),
        });

        try {
            const response = await this.makeRequest(`${config.chat.getSessions}?${params}`, {
                method: 'GET'
            });

            return response;
        } catch (error) {
            console.error('Error fetching chat sessions:', error);
            
            if (error.status === 401) {
                throw new Error('Authentication failed. Please log in again.');
            }
            
            throw new Error('Failed to load chat sessions. Please try again.');
        }
    }
}

// Export singleton instance
export const chatApi = new ChatApiClient();
export default chatApi; 